import React from 'react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import { isObject, toInlineString } from './helper';
import RecommendationDynamicTable, { canRenderDynamicTableValue } from './RecommendationDynamicTable';
import RecommendationMarkdown from './RecommendationMarkdown';
import { AIInsightSolution } from '../../types/recommendations';
import { sortByOrder } from '../../utils/helpers/object';

const Container = styled.div`
  a {
    color: var(--dark-blue);
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;
  }
`;

const ParagraphTitle = styled.div`
  font-size: 18px;
  font-family: var(--font-bold);
  margin-top: 40px;
  margin-bottom: 14px;
`;

const SubParagraphTitle = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
  margin-bottom: 9px;
`;

interface PaddedDivProps {
  indentation?: number;
}

const INDENT_SIZE = 20;

const PaddedDiv = styled.div<PaddedDivProps>`
  padding-left: ${({ indentation }) => (indentation ? `${INDENT_SIZE * indentation}px` : 'unset')};
`;

type Props = {
  structure: AIInsightSolution;
  communityId?: number;
  communityName?: string;
};

const humanizeKey = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
const objectToMarkdown = (value: Record<string, any>): string => {
  return Object.entries(value)
    .map(([key, val]) => `**${humanizeKey(key)}:** ${toInlineString(val)}`)
    .join('\n\n');
};

const renderStructuredValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <RecommendationMarkdown>{String(value)}</RecommendationMarkdown>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }

    const isPrimitiveArray = value.every(
      (item) => item === null || ['string', 'number', 'boolean'].includes(typeof item)
    );

    if (isPrimitiveArray) {
      const markdownList = value.map((item) => `* ${String(item ?? '')}`).join('\n');
      return <RecommendationMarkdown>{markdownList}</RecommendationMarkdown>;
    }

    if (canRenderDynamicTableValue(value)) {
      return <RecommendationDynamicTable value={value} />;
    }

    return (
      <div>
        {value.map((item, index) => (
          <div key={index}>
            {isObject(item) ? (
              <RecommendationMarkdown>{objectToMarkdown(item)}</RecommendationMarkdown>
            ) : (
              renderStructuredValue(item)
            )}
          </div>
        ))}
      </div>
    );
  }

  if (isObject(value)) {
    return <RecommendationMarkdown>{objectToMarkdown(value)}</RecommendationMarkdown>;
  }

  return <RecommendationMarkdown>{String(value)}</RecommendationMarkdown>;
};

const RecommendationStructuredView = ({ structure, communityId, communityName }: Props) => {
  const sortedInsights = sortByOrder(Object.entries(structure));
  return (
    <Container>
      {sortedInsights.map(([key, field]) => {
        const value = field.value;
        let paragraphContent: React.ReactNode = null;

        if (isObject(value)) {
          paragraphContent = (
            <div>
              {sortByOrder(Object.entries(value)).map(([subKey, subField]) => {
                if (!isObject(subField)) {
                  return <div key={subKey}>{renderStructuredValue(subField)}</div>;
                }

                return (
                  <div key={subKey}>
                    {subField.title ? <SubParagraphTitle>{String(subField.title)}</SubParagraphTitle> : null}
                    {renderStructuredValue(subField.value)}
                  </div>
                );
              })}
            </div>
          );
        } else {
          paragraphContent = renderStructuredValue(value);
        }

        // Handle community link substitution
        let title: React.ReactNode = field.title;
        if (communityId && communityName && field.title?.includes('[Community Name]')) {
          const [before, after] = field.title.split('[Community Name]');
          title = (
            <>
              {before}
              <Link to={`/community/${communityId}`}>{communityName}</Link>
              {after}
            </>
          );
        }

        return (
          <div key={key}>
            <ParagraphTitle>{title}</ParagraphTitle>
            <PaddedDiv indentation={field.indentation_level}>{paragraphContent}</PaddedDiv>
          </div>
        );
      })}
    </Container>
  );
};

export default RecommendationStructuredView;
